import React, {FC, useEffect, useRef, useState} from 'react';

import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import classNames from 'classnames';
import {t} from 'i18next';

import ChevronLeftIcon from 'assets/icons/chevronLeft';
import {ChevronRight} from 'assets/icons/chevronRightCard';
import ReactNative from 'services/react-native';
import {debounce} from 'utils/helper';

import {
  HorizontalScrollerContainer,
  HorizontalScrollerWrapper,
  HorizontalScrollerContent,
  LeftCircularArrowButton,
  RightCircularArrowButton,
  LeftArrowHolder,
  RightArrowHolder,
  SeeAllContainer,
  SeeAllWrapperContainer,
  SeeAllText,
  SeeAllInnerWrapper
} from './styles';

export type HorizontalScrollerProps = {
  children: React.ReactNode;
  onClickLeftScrollButton?: () => void;
  onClickRightScrollButton?: () => void;
  showShadow?: boolean;
  onSeeAllClickHandler?: () => void;
  triggerCarouselScrollAnalytics?: () => void;
  scrollButtonAlignmentType?: ScrollButtonAlignmentType | undefined;
  isShowSeeAll?: boolean;
};

export enum ScrollButtonAlignmentType {
  BRAND_CAROUSEL = 'brandCarousel'
}

export const HorizontalScroller: FC<HorizontalScrollerProps> = ({
  children,
  onClickLeftScrollButton,
  onClickRightScrollButton,
  showShadow = true,
  onSeeAllClickHandler,
  scrollButtonAlignmentType,
  triggerCarouselScrollAnalytics,
  isShowSeeAll
}) => {
  const theme = useTheme();
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const hasScrolled = useRef(false);
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const isMobileScreen = isReactNativeView || isMobileView;

  const [displayLeftButton, setDisplayLeftButton] = useState(false);
  const [displayRightButton, setDisplayRightButton] = useState(false);

  const LeftShadowWrapper = showShadow ? LeftArrowHolder : React.Fragment;
  const RightShadowWrapper = showShadow ? RightArrowHolder : React.Fragment;

  const setManualScroll = (sign: number) => {
    const scrollElementRefCurrent = scrollElementRef.current;
    if (scrollElementRefCurrent) {
      const movingDistance = scrollElementRefCurrent.scrollLeft + sign * scrollElementRefCurrent.clientWidth;
      scrollElementRefCurrent.scrollTo({left: movingDistance, behavior: 'smooth'});
    }
  };

  const setButtonStatus = () => {
    const scrollElementRefCurrent = scrollElementRef.current;

    if (scrollElementRefCurrent) {
      const scrolledLeft = scrollElementRefCurrent.scrollLeft;
      const containerWidth = scrollElementRefCurrent.clientWidth;
      const scrollWidth = scrollElementRefCurrent.scrollWidth;

      setDisplayLeftButton(scrolledLeft > 0);
      setDisplayRightButton(Math.floor(scrollWidth - scrolledLeft) > containerWidth);
    }
  };

  useEffect(() => {
    hasScrolled.current = false;
    scrollElementRef?.current?.addEventListener('scroll', setButtonStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => scrollElementRef?.current?.removeEventListener('scroll', setButtonStatus);
  }, []);

  useEffect(() => {
    setButtonStatus();
  }, []);

  const handleOnLeftClick = () => {
    onClickLeftScrollButton && onClickLeftScrollButton();
    setManualScroll(-1);
  };

  const handleOnRightClick = () => {
    onClickRightScrollButton && onClickRightScrollButton();
    setManualScroll(1);
  };

  const handalScroll = (e: any) => {
    if (!hasScrolled.current && e.target) {
      debouncedLog();
      hasScrolled.current = true;
    }
  };

  const debouncedLog = debounce(() => {
    triggerCarouselScrollAnalytics && triggerCarouselScrollAnalytics();
  }, 5000);

  const renderSeeAll = () => (
    <SeeAllContainer>
      <SeeAllWrapperContainer onClick={onSeeAllClickHandler}>
        <SeeAllInnerWrapper>
          <RightCircularArrowButton className="shadow seeAll">
            <ChevronRight fillColor={theme.palette.grey[900]} width={26} height={26} />
          </RightCircularArrowButton>
          <SeeAllText>{t('global.seeAll')}</SeeAllText>
        </SeeAllInnerWrapper>
      </SeeAllWrapperContainer>
    </SeeAllContainer>
  );

  return (
    <HorizontalScrollerContainer data-testid="horizontal-scroller-container">
      <HorizontalScrollerWrapper>
        <HorizontalScrollerContent onScroll={(e) => handalScroll(e)} ref={scrollElementRef}>
          {
            <>
              <>{children}</>
              {isShowSeeAll && renderSeeAll()}
            </>
          }
        </HorizontalScrollerContent>
      </HorizontalScrollerWrapper>
      {displayLeftButton && !isMobileScreen && (
        <LeftShadowWrapper>
          <LeftCircularArrowButton
            data-testid="horizontal-scroller-left-button"
            className={classNames({shadow: showShadow, [scrollButtonAlignmentType as string]: true})}
            onClick={handleOnLeftClick}
          >
            <ChevronLeftIcon fillColor={theme.palette.grey[900]} />
          </LeftCircularArrowButton>
        </LeftShadowWrapper>
      )}
      {displayRightButton && !isMobileScreen && (
        <RightShadowWrapper>
          <RightCircularArrowButton
            data-testid="horizontal-scroller-right-button"
            className={classNames({shadow: showShadow, [scrollButtonAlignmentType as string]: true})}
            onClick={handleOnRightClick}
          >
            <ChevronRight fillColor={theme.palette.grey[900]} />
          </RightCircularArrowButton>
        </RightShadowWrapper>
      )}
    </HorizontalScrollerContainer>
  );
};
